import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Auto-advances a stories-banner section: the active story circle's ring
 * sweep (a CSS animation, see sections/stories-banner.liquid) is the story
 * timer — when it completes, the inner slideshow advances to the next story.
 *
 * Inverting control this way keeps the ring and the slide perfectly in sync:
 * pausing the animation (hover / keyboard focus, via CSS animation-play-state)
 * pauses the advance too, unlike slideshow-component's own setInterval-based
 * autoplay, which skips ticks while hovered and would drift from a
 * fixed-duration animation.
 *
 * The `auto-advance` attribute gates both this handler and the CSS animation;
 * it is omitted in the theme editor. With reduced motion the animation never
 * runs (no animationend fires), so stories only change on click.
 */
class StoriesBanner extends Component {
  // slideshow.js flips aria-hidden/reveal on the slide roots on every
  // advance; ignore those so production navigation never triggers a refresh.
  #slideObserver = new MutationObserver((mutations) => {
    const relevant = mutations.some(
      (m) => !(m.type === 'attributes' && (m.attributeName === 'aria-hidden' || m.attributeName === 'reveal'))
    );
    if (relevant) this.#scheduleThumbnailRefresh();
  });

  /** @type {number | null} */
  #thumbnailRefresh = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('animationend', this.#handleAnimationEnd);
    this.#populateThumbnails();
    this.#observeSlides();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('animationend', this.#handleAnimationEnd);
    this.#slideObserver.disconnect();
    if (this.#thumbnailRefresh) cancelAnimationFrame(this.#thumbnailRefresh);
  }

  /**
   * The circles strip belongs to the section, but theme block settings are
   * only readable inside the block file (from the section, section.blocks
   * exposes opaque "@theme" entries), so each _story slide SSRs its own
   * thumbnail in an inert <template> and we clone it into the matching
   * circle here, replacing the SSR'd placeholder.
   */
  #populateThumbnails() {
    const slides = this.querySelectorAll('.stories-banner__slide');
    const gaps = this.querySelectorAll('.stories-banner__story-gap');

    slides.forEach((slide, index) => {
      const thumbnail = slide.querySelector('template[data-story-thumbnail]');
      const gap = gaps[index];

      if (thumbnail instanceof HTMLTemplateElement && gap) {
        gap.replaceChildren(thumbnail.content.cloneNode(true));
      }
    });
  }

  /**
   * Block-granular re-renders (a theme editor setting tweak, the CLI's hot
   * reload) patch the slide DOM in place without reconnecting this component
   * and without firing any shopify:* event, leaving the cloned circle stale.
   * Watching the slides subtree catches those; full section re-renders
   * reconnect the component and repopulate via connectedCallback instead.
   * The strip lives outside the observed subtree, so repopulating can't
   * retrigger the observer.
   */
  #observeSlides() {
    const slides = this.querySelector('.stories-banner__slides');
    if (slides) this.#slideObserver.observe(slides, { childList: true, subtree: true, attributes: true });
  }

  #scheduleThumbnailRefresh() {
    if (this.#thumbnailRefresh) cancelAnimationFrame(this.#thumbnailRefresh);
    this.#thumbnailRefresh = requestAnimationFrame(() => {
      this.#thumbnailRefresh = null;
      this.#populateThumbnails();
    });
  }

  /** @param {AnimationEvent} event */
  #handleAnimationEnd = (event) => {
    if (event.animationName !== 'stories-banner-ring') return;
    if (!this.hasAttribute('auto-advance') || prefersReducedMotion()) return;

    this.querySelector('slideshow-component')?.next();
  };
}

if (!customElements.get('stories-banner-component')) {
  customElements.define('stories-banner-component', StoriesBanner);
}
